import { Typography } from "@/components/essentials/typography";
import { PlanCardContainer } from "@/components/fragments/plan";
import { useApolloError } from "@/hooks/useApolloError";
import { AuthedLayout } from "@/layouts";
import { Box, Skeleton, Stack } from "@chakra-ui/react";
import { type NextPage } from "next";
import { NextSeo } from "next-seo";
import { useRouter } from "next/router";
import { FormProvider, useForm } from "react-hook-form";
import { skeletonTestId } from "./const";
import { ContractFilter } from "./__components__/ContractFilter/ContractFilter";
import {
  shipmentFilterYearName,
  ShipmentYearFilterForm,
} from "./__components__/ShipmentYearFilterForm/ShipmentYearFilterForm";
import { type ShipmentYearFilterFormValue } from "./__components__/ShipmentYearFilterForm/type";
import { useGetAllDataPagesPlansHistoryQuery } from "./__gql__/get-all-data.gen";
import {
  getContractIdFromQuery,
  getContractMenuItem,
  getViewableShipmentPlansByContractedPlan,
  getYears,
  getLatestShipmentYear,
} from "./module";
import { isQueryCompleted } from "@/modules/utils/apollo-wrapper";
import { formatAsiaTokyoDateToString } from "@/modules/utils/date";
import { Empty } from "./__components__/Empty/Empty";

const PlansHistoryPage: NextPage = () => {
  const methods = useForm<ShipmentYearFilterFormValue>({
    defaultValues: { shipmentYearFilter: "" },
  });
  // security/detect-non-literal-fs-filename を防ぐために const shipmentYearFilter を使わない
  const filterYear = methods.watch("shipmentYearFilter");

  const router = useRouter();
  const contractId = getContractIdFromQuery(router);

  const resultGetAllData = useGetAllDataPagesPlansHistoryQuery({
    skip: !router.isReady,
    variables: {
      familyCustomerContractedPlanUuids: [getContractIdFromQuery(router)],
    },
    onCompleted: (data) => {
      methods.setValue(
        shipmentFilterYearName,
        getLatestShipmentYear(getViewableShipmentPlansByContractedPlan(contractId, data.shipments)) ?? "",
      );
    },
  });

  useApolloError(resultGetAllData.error);
  const viewableShipmentPlans = getViewableShipmentPlansByContractedPlan(contractId, resultGetAllData.data?.shipments);

  return (
    <>
      <NextSeo title="おもちゃ履歴" description="Toysub!から送られたおもちゃの履歴をご確認できます。" />
      <AuthedLayout data-testid="pages/plans/history" pt="1.5rem" px="1rem">
        <Typography variant="display3">おもちゃ履歴</Typography>
        <Typography variant="text4" color="gray.dark" mb={6}>
          これまで届いたおもちゃが表示されます。
        </Typography>
        {!isQueryCompleted([resultGetAllData]) && <Skeleton data-testid={skeletonTestId} height="50%" />}
        {isQueryCompleted([resultGetAllData]) && (
          <div>
            {viewableShipmentPlans?.length === 0 && <Empty />}
            {viewableShipmentPlans?.length !== 0 && (
              <div>
                <Box mb={2}>
                  <ContractFilter
                    contract={getContractMenuItem(
                      contractId,
                      resultGetAllData.data?.familyCustomer.familyCustomerContractedPlans,
                    )}
                  />
                </Box>
                <Box mb={6}>
                  <FormProvider {...methods}>
                    <ShipmentYearFilterForm years={getYears(viewableShipmentPlans)} />
                  </FormProvider>
                </Box>
                <Stack spacing={20}>
                  {viewableShipmentPlans
                    ?.filter((shipment) => formatAsiaTokyoDateToString(shipment.shippedAt, "yyyy年") === filterYear)
                    .map((shipmentPlan) => (
                      <PlanCardContainer key={shipmentPlan.uuid} shipmentPlan={shipmentPlan} />
                    ))}
                </Stack>
              </div>
            )}
          </div>
        )}
      </AuthedLayout>
    </>
  );
};

export default PlansHistoryPage;
